import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Badge, Button, Card, Field, SectionHeader } from '@pixlide/ui';
import { ApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth-store';
import {
  useDisableTwoFactor,
  useEnableTwoFactor,
  useRequestTwoFactorEmailCode,
  useSetupTwoFactor,
} from '@/api/queries/auth';

/**
 * Activation de la double authentification — obligatoire pour tout compte PlatformAdmin.
 *
 * L'API refuse désormais toute action nécessitant la permission `platform:admin` (donc en
 * pratique tout le reste du back-office) tant que ce compte n'a pas la 2FA active — voir
 * `AuthorizationBehavior` côté API. Cette page reste accessible dans cet état : les commandes
 * `SetupTwoFactorCommand` / `EnableTwoFactorCommand` n'exigent qu'une session authentifiée,
 * pas cette permission.
 *
 * Port quasi identique de `frontend/src/features/settings/ParametresPage.tsx`
 * (`TwoFactorSection`), qui couvre le même besoin côté compte client.
 */
export function SecurityPage() {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <SectionHeader
        title="Sécurité"
        description="La double authentification est obligatoire pour tout compte back-office Pixlide."
      />

      <Card className="p-6">
        <TwoFactorSection />
      </Card>
    </div>
  );
}

type TwoFactorStep =
  | { kind: 'idle' }
  | { kind: 'awaitingEmailCode' }
  | { kind: 'enrolling'; secret: string; provisioningUri: string }
  | { kind: 'recoveryCodes'; codes: readonly string[] };

/**
 * Activation en trois temps : un code envoyé par email prouve d'abord l'accès à la boîte
 * mail, avant de configurer et confirmer l'application d'authentification. Sans cette étape,
 * un attaquant qui a déjà pris la main sur une session pourrait activer la 2FA avec SON
 * application, verrouillant l'admin légitime hors de son propre compte.
 */
function TwoFactorSection() {
  const user = useAuthStore((state) => state.user);
  const requestEmailCode = useRequestTwoFactorEmailCode();
  const setup = useSetupTwoFactor();
  const enable = useEnableTwoFactor();
  const disable = useDisableTwoFactor();

  const [step, setStep] = useState<TwoFactorStep>({ kind: 'idle' });
  const [emailCode, setEmailCode] = useState('');
  const [code, setCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  async function startEmailVerification() {
    await requestEmailCode.mutateAsync();
    setStep({ kind: 'awaitingEmailCode' });
  }

  async function startEnrollment() {
    const result = await setup.mutateAsync();
    setStep({ kind: 'enrolling', secret: result.secret, provisioningUri: result.provisioningUri });
  }

  async function confirmEnrollment() {
    const result = await enable.mutateAsync({ emailCode, code });
    setEmailCode('');
    setCode('');
    setStep({ kind: 'recoveryCodes', codes: result.recoveryCodes });
  }

  async function confirmDisable() {
    await disable.mutateAsync({ password: disablePassword, code: disableCode });
    setDisablePassword('');
    setDisableCode('');
    setShowDisableForm(false);
  }

  const emailCodeError = requestEmailCode.error instanceof ApiError ? requestEmailCode.error.message : null;
  const enableError = enable.error instanceof ApiError ? enable.error.message : null;
  const disableError = disable.error instanceof ApiError ? disable.error.message : null;

  if (step.kind === 'recoveryCodes') {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium text-ink-900">2FA activée</p>
          <p className="text-xs text-ink-500">
            Conservez ces codes de récupération dans un endroit sûr : chacun ne fonctionne qu'une
            fois, et ils ne seront plus jamais affichés.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-input border border-ink-100 bg-ink-50/50 p-4 font-mono text-sm">
          {step.codes.map((recoveryCode) => (
            <span key={recoveryCode}>{recoveryCode}</span>
          ))}
        </div>

        <Button size="sm" className="self-start" onClick={() => setStep({ kind: 'idle' })}>
          J'ai noté mes codes
        </Button>
      </div>
    );
  }

  if (step.kind === 'awaitingEmailCode') {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium text-ink-900">Vérifiez votre adresse email</p>
          <p className="text-xs text-ink-500">
            Un code à 6 chiffres vient d'être envoyé à votre adresse email. Il prouve l'accès à
            votre boîte mail avant de configurer l'application d'authentification.
          </p>
        </div>

        <Field
          label="Code reçu par email"
          inputMode="numeric"
          autoFocus
          placeholder="123456"
          error={emailCodeError ?? undefined}
          value={emailCode}
          onChange={(event) => {
            setEmailCode(event.target.value.replace(/\D/g, ''));
            requestEmailCode.reset();
          }}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setEmailCode('');
              setStep({ kind: 'idle' });
            }}
          >
            Annuler
          </Button>
          <Button
            size="sm"
            isLoading={setup.isPending}
            disabled={emailCode.length !== 6}
            onClick={() => void startEnrollment()}
          >
            Continuer
          </Button>
        </div>
      </div>
    );
  }

  if (step.kind === 'enrolling') {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium text-ink-900">Configurer votre application d'authentification</p>
          <p className="text-xs text-ink-500">
            Scannez ce QR code avec Google Authenticator, Authy ou 1Password — ou saisissez le
            secret manuellement si vous préférez — puis entrez le code généré.
          </p>
        </div>

        <div className="flex justify-center rounded-input border border-ink-100 bg-white p-4">
          <QRCodeSVG value={step.provisioningUri} size={176} level="M" />
        </div>

        <div className="rounded-input border border-ink-100 bg-ink-50/50 p-3">
          <p className="text-xs text-ink-500">Secret (saisie manuelle)</p>
          <p className="mt-1 break-all font-mono text-sm text-ink-900">{step.secret}</p>
        </div>

        <Field
          label="Code à 6 chiffres"
          inputMode="numeric"
          autoFocus
          placeholder="123456"
          error={enableError ?? undefined}
          value={code}
          onChange={(event) => {
            setCode(event.target.value.replace(/\D/g, ''));
            enable.reset();
          }}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setEmailCode('');
              setCode('');
              setStep({ kind: 'idle' });
            }}
          >
            Annuler
          </Button>
          <Button size="sm" isLoading={enable.isPending} onClick={() => void confirmEnrollment()}>
            Activer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-ink-900">Authentification à deux facteurs</p>
          <p className="text-xs text-ink-500">
            {user?.twoFactorEnabled
              ? "Activée : un code de votre application d'authentification est demandé à chaque connexion."
              : "Obligatoire pour ce compte : tant qu'elle n'est pas active, aucune action back-office n'est possible."}
          </p>
        </div>

        {user?.twoFactorEnabled ? (
          <Badge tone="brand">Activée</Badge>
        ) : (
          <Button size="sm" isLoading={requestEmailCode.isPending} onClick={() => void startEmailVerification()}>
            Activer
          </Button>
        )}
      </div>

      {user?.twoFactorEnabled ? (
        showDisableForm ? (
          <div className="flex flex-col gap-3 sm:max-w-sm">
            <p className="text-xs text-ink-500">
              Le mot de passe seul ne suffit pas à retirer ce facteur de sécurité : présentez
              aussi un code de votre application d'authentification (ou un code de
              récupération).
            </p>
            <Field
              label="Mot de passe"
              type="password"
              autoComplete="current-password"
              autoFocus
              error={disableError ?? undefined}
              value={disablePassword}
              onChange={(event) => {
                setDisablePassword(event.target.value);
                disable.reset();
              }}
            />
            <Field
              label="Code de l'application d'authentification ou de récupération"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={disableCode}
              onChange={(event) => {
                setDisableCode(event.target.value.replace(/\D/g, ''));
                disable.reset();
              }}
            />
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDisablePassword('');
                  setDisableCode('');
                  setShowDisableForm(false);
                }}
              >
                Annuler
              </Button>
              <Button
                size="sm"
                isLoading={disable.isPending}
                disabled={!disablePassword || !disableCode}
                onClick={() => void confirmDisable()}
              >
                Désactiver la 2FA
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() => setShowDisableForm(true)}
          >
            Désactiver la 2FA
          </Button>
        )
      ) : null}
    </div>
  );
}
